// **Eigene Auswahlfelder.**
//
// Ein natives `<select>` legt Chrome bei kurzer Liste ÜBER das Feld, mit einem
// Eintrag genau unter dem Cursor. Das Aufklappen geschieht beim Drücken der
// Maustaste, das Loslassen liegt dann schon über einem Eintrag — und zählt als
// Auswahl. Ein einfacher Klick ändert damit den Wert, ohne dass jemand etwas
// gewählt hätte. Die Platzierung macht allein der Browser; aus dem Stilblatt
// ist daran nichts zu ändern.
//
// Deshalb ein Menü wie jedes andere: Es öffnet auf **click** — der Mausklick,
// der es aufmacht, ist da bereits verbraucht, ein Eintrag kann von ihm nicht
// mehr getroffen werden. Das Menü hängt am `<body>` (Portal), liegt nie über
// dem Knopf, schließt bei einem Klick daneben, beim Scrollen außerhalb seiner
// Liste und mit Escape; die Pfeiltasten wandern durch die Einträge.
//
// Anders als die Vanilla-Fassung braucht es kein verstecktes `<select>` mehr:
// Niemand liest `.value` außerhalb der Komponente, der Wert ist Zustand.

import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { moveFocus, usePopover } from "../hooks/use-popover";

export type SelectOptions = ReadonlyArray<readonly [value: string, label: string]>;

/**
 * Der Wert, den ein natives `<select>` zeigen würde: der passende Eintrag,
 * sonst der erste. Die Formulare rechnen ihren Anfangszustand damit, damit
 * ein gespeicherter Wert, den es nicht mehr gibt, wie früher zum ersten wird.
 */
export function pickOption(value: string | number | null | undefined, options: SelectOptions): string {
  const v = String(value ?? "");
  return options.some(([o]) => o === v) ? v : (options[0]?.[0] ?? "");
}

export function SelectMenu({
  value, options, onChange, disabled = false, className = "pf-btn pf-select", title, ariaLabel, name,
}: {
  value: string;
  options: SelectOptions;
  onChange: (value: string) => void;
  disabled?: boolean;
  className?: string;
  title?: string;
  ariaLabel?: string;
  /** Nur zur Orientierung in Tests und für Werkzeuge (`data-name`). */
  name?: string;
}) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const close = useCallback(() => setOpen(false), []);
  const style = usePopover({ open, onClose: close, anchorRef, menuRef });
  const current = options.find(([v]) => v === value) ?? options[0];

  // Beim Öffnen steht der Fokus auf dem gewählten Eintrag (oder dem ersten),
  // damit die Pfeiltasten von dort aus wandern.
  useEffect(() => {
    if (!open) return;
    const menu = menuRef.current;
    (menu?.querySelector<HTMLButtonElement>(".pf-select-menu__opt--on") ?? menu?.querySelector<HTMLButtonElement>("button"))?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (moveFocus(menuRef.current, e.key)) e.preventDefault(); };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [open]);

  const choose = (v: string) => {
    setOpen(false);
    anchorRef.current?.focus();
    if (v !== value) onChange(v);
  };

  return (
    <>
      <button
        ref={anchorRef} type="button" className={className} title={title} aria-label={ariaLabel} data-name={name}
        aria-haspopup="listbox" aria-expanded={open} disabled={disabled}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="pf-select__label">{current?.[1] ?? ""}</span>
        <span className="pf-select__caret" aria-hidden="true">▾</span>
      </button>
      {open && createPortal(
        <div ref={menuRef} className="pf-select-menu" role="listbox" style={style}>
          {options.map(([v, label]) => {
            const on = v === current?.[0];
            return (
              <button
                key={v} type="button" role="option" aria-selected={on}
                className={`pf-select-menu__opt${on ? " pf-select-menu__opt--on" : ""}`}
                onClick={() => choose(v)}
              >{label}</button>
            );
          })}
        </div>,
        document.body,
      )}
    </>
  );
}
